/** /reseller_policies/ — compile a tenant's reseller suppression matrix.
 *
 * Ops maintains `do_not_send_{concern}_{channel}` flags by hand on reseller tag
 * data. Compiling turns that matrix into feature-flag and delivery-blacklist
 * rows that actually enforce it, instead of every standard block re-checking
 * the same booleans.
 *
 * `preview` and `compile` return the same report; only one of them writes.
 * There is deliberately no compile-everything endpoint — compiling changes
 * which messages real guests receive, so it always names one tenant.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules).
 */
import { apiGet, apiPost } from './client';

/** What happened (or would happen) to one compiled row. */
export type PolicyOutcomeStatus =
  | 'created'
  | 'updated'
  | 'unchanged'
  | 'diverged'
  | 'forced'
  | 'skipped';

/** A reseller tag a compiled row's chips name. */
export interface PolicyTagRef {
  id: number;
  name: string;
}

export interface PolicyOutcome {
  status: PolicyOutcomeStatus;
  kind: 'feature_flag' | 'blacklist_rule';
  label: string;
  reason: string;
  row_id: number | null;
  /**
   * The reseller tags this row covers. Group by these to answer "what does this
   * reseller currently enforce" — `label` answers the other direction.
   */
  tags: PolicyTagRef[];
}

export interface PolicyReport {
  /** Tenant code. */
  tour_operator: string;
  tags_read: number;
  outcomes: PolicyOutcome[];
  /**
   * Declared policy the platform cannot act on — chat and mobile have no
   * blacklist home, `social_media` has no gateable feature, `balance_due` names
   * a notification the pack does not ship. Shown, never dropped: ops believes
   * this policy is in force.
   */
  unenforceable: string[];
  /** Suppression keys the standard grammar does not recognise. */
  unknown_keys: string[];
  /**
   * Values that are not real booleans, with how they were read. Values use the
   * SAME truthiness the templates apply today, so `"0"` and `"false"` are
   * non-empty strings and therefore TRUE.
   */
  ambiguous_values: string[];
}

export interface PolicyDriftRow {
  tour_operator_id: number;
  tour_operator: string;
  /** Rows a recompile would create or update. */
  pending: string[];
  unenforceable: string[];
}

export interface PolicyDriftResponse {
  count: number;
  results: PolicyDriftRow[];
}

/** What a compile WOULD do. Writes nothing. */
export function preview(tourOperatorId: number): Promise<PolicyReport> {
  return apiGet<PolicyReport>(`/reseller_policies/?tour_operator_id=${tourOperatorId}`);
}

/** Compile one tenant. `force` names concerns whose DIVERGED rows to overwrite. */
export function compile(tourOperatorId: number, force: string[] = []): Promise<PolicyReport> {
  return apiPost<PolicyReport>('/reseller_policies/compile/', {
    tour_operator_id: tourOperatorId,
    force,
  });
}

/** Tenants that have already compiled and whose rows no longer match their data. */
export function drift(): Promise<PolicyDriftResponse> {
  return apiGet<PolicyDriftResponse>('/reseller_policies/drift/');
}
